import { ChangeEvent, useEffect, useRef, useState } from 'react';

import { ImageCropModal } from '../ImageCropModal';

type ImageCropInputProps = {
  id: string;
  /** Nome acessível do modal de recorte. */
  label: string;
  className?: string;
  /** Recebe o recorte (JPEG) e um object URL dele para a miniatura. */
  onCrop: (image: Blob, previewUrl: string) => void;
};

/** Proporção com que a moldura abre; as alças podem mudá-la. */
const INITIAL_ASPECT = 0.7;

/**
 * Campo de arquivo que abre o modal de recorte ao escolher uma imagem.
 * O formulário só recebe o recorte em "Salvar"; "Cancelar" e Esc deixam o
 * valor anterior intacto. Nos dois casos o campo é limpo, para o mesmo
 * arquivo poder reabrir o modal.
 */
export function ImageCropInput({
  id,
  label,
  className,
  onCrop,
}: ImageCropInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const wasOpenRef = useRef(false);
  const [imageSrc, setImageSrc] = useState('');

  // Revoga o object URL ao trocar de imagem, ao fechar e ao desmontar.
  useEffect(() => {
    if (!imageSrc) return;
    return () => URL.revokeObjectURL(imageSrc);
  }, [imageSrc]);

  // O foco volta só depois do desmonte do modal: antes disso o app ainda
  // está `inert` e o campo não aceita foco.
  useEffect(() => {
    if (imageSrc) {
      wasOpenRef.current = true;
      return;
    }
    if (wasOpenRef.current) {
      wasOpenRef.current = false;
      inputRef.current?.focus();
    }
  }, [imageSrc]);

  function clearInput() {
    if (inputRef.current) inputRef.current.value = '';
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      clearInput();
      return;
    }
    setImageSrc(URL.createObjectURL(file));
  }

  const handleCancel = () => {
    clearInput();
    setImageSrc('');
  };

  const handleConfirm = (cropped: Blob) => {
    onCrop(cropped, URL.createObjectURL(cropped));
    clearInput();
    setImageSrc('');
  };

  return (
    <>
      <input
        ref={inputRef}
        className={className}
        id={id}
        type="file"
        accept="image/*"
        onChange={handleChange}
      />

      {imageSrc && (
        <ImageCropModal
          imageSrc={imageSrc}
          initialAspect={INITIAL_ASPECT}
          label={label}
          onCancel={handleCancel}
          onConfirm={handleConfirm}
        />
      )}
    </>
  );
}
